import { CardDetailsDto } from "./dto/CardDetails.dto";
import mcLogo from "../../../assets/images/mclogo-for-footer1.svg";
import amexLogo from "../../../assets/images/amex_logo.png";
import discoverLogo from "../../../assets/images/discover.jpg";
import visaLogo from "../../../assets/images/visa-blue.webp";
import dinersLogo from "../../../assets/images/DC_logo.png";
import jcbLogo from "../../../assets/images/jcb_Logo.jpg";
import upLogo from "../../../assets/images/up_logo.png";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { IoIosCard } from "react-icons/io";

interface CardPreviewProps {
    card: CardDetailsDto | undefined;
    status?: "Active" | "Inactive" | "Expired";
}

export default function CardPreview({
    card,
    status = "Active",
}: CardPreviewProps) {
    const isMobile = useIsMobile(900);

    // Get card brand icon/logo
    const getCardIcon = (brand: string) => {
        switch (brand.toLowerCase()) {
            case "mastercard":
                return <img className="h-6" src={mcLogo} alt="Mastercard" />;
            case "visa":
                return <img className="h-4" src={visaLogo} alt="Visa" />;
            case "amex":
            case "american express":
                return <img className="h-8" src={amexLogo} alt="Amex" />;
            case "discover":
                return (
                    <img className="h-8" src={discoverLogo} alt="Discover" />
                );
            case "diners":
            case "diners club":
                return <img className="h-6" src={dinersLogo} alt="Diners" />;
            case "jcb":
                return <img className="h-6" src={jcbLogo} alt="JCB" />;
            case "unionpay":
                return <img className="h-6" src={upLogo} alt="Unionpay" />;
            default:
                return <IoIosCard size={30} className="text-secondary" />;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case "active":
                return "text-green-600";
            case "expired":
                return "text-red-600";
            case "inactive":
                return "text-gray-600";
            default:
                return "text-gray-600";
        }
    };

    const formatExpiry = (month: number, year: number) => {
        const monthStr = month.toString().padStart(2, "0");
        const yearStr = year.toString();
        return `${monthStr}/${yearStr}`;
    };

    console.log("card", card);

    return (
        <>
            {isMobile ? (
                <>
                    {/* Mobile layout: label on left, value on right */}
                    <div className="md:hidden divide-y border-b border-gray-100">
                        <div className="flex items-center justify-between py-3 px-4">
                            <span className="text-sm text-gray-600">
                                Payment method
                            </span>
                            <div className="font-medium capitalize flex flex-col gap-0 justify-center items-end whitespace">
                                {getCardIcon(card?.brand || "")}
                                {card && card?.brand?.length > 6 ? (
                                    <>
                                        <span>
                                            {card?.brand === "jcb"
                                                ? "JCB"
                                                : card?.brand}
                                        </span>
                                        <span>****{card?.last4}</span>
                                    </>
                                ) : (
                                    <>
                                        <span>
                                            {card && card?.brand === "jcb"
                                                ? "JCB"
                                                : card?.brand}{" "}
                                            ****{card?.last4}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center justify-between py-3 px-4">
                            <span className="text-sm text-gray-600">
                                Expiration Date
                            </span>
                            <span>
                                {formatExpiry(
                                    card?.expMonth || 0,
                                    card?.expYear || 0
                                )}
                            </span>
                        </div>
                        <div className="flex items-center justify-between py-3 px-4">
                            <span className="text-sm text-gray-600">
                                Status
                            </span>
                            <span
                                className={`inline-flex items-center ${getStatusColor(
                                    status
                                )}`}
                            >
                                <span
                                    className={`w-2 h-2 rounded-full mr-2 ${
                                        status.toLowerCase() === "active"
                                            ? "bg-green-500"
                                            : status.toLowerCase() === "expired"
                                            ? "bg-red-500"
                                            : "bg-gray-500"
                                    }`}
                                />
                                {status}
                            </span>
                        </div>
                        <div className="flex items-center justify-between py-3 px-4">
                            <span className="text-sm text-gray-600">Type</span>
                            <span>
                                {card?.funding
                                    ? `${
                                          card.funding.charAt(0).toUpperCase() +
                                          card.funding.slice(1)
                                      } card`
                                    : "Credit card"}
                            </span>
                        </div>
                    </div>

                    {/* Desktop layout: single row grid aligned to header */}
                    <div className="hidden md:grid py-3 px-4 border-b border-gray-100 hover:bg-gray-50 md:[grid-template-columns:2fr_1fr_1fr_1fr]">
                        {/* Payment Method */}
                        <div className="flex items-center gap-3">
                            {getCardIcon(card?.brand || "")}
                            <span className="font-medium capitalize">
                                {card?.brand} ****{card?.last4}
                            </span>
                        </div>
                        {/* Expiration Date */}
                        <div className="text-center">
                            {formatExpiry(
                                card?.expMonth || 0,
                                card?.expYear || 0
                            )}
                        </div>
                        {/* Status */}
                        <div className="text-center">
                            <span
                                className={`inline-flex items-center ${getStatusColor(
                                    status
                                )}`}
                            >
                                <span
                                    className={`w-2 h-2 rounded-full mr-2 ${
                                        status.toLowerCase() === "active"
                                            ? "bg-green-500"
                                            : status.toLowerCase() === "expired"
                                            ? "bg-red-500"
                                            : "bg-gray-500"
                                    }`}
                                />
                                {status}
                            </span>
                        </div>
                        {/* Type */}
                        <div className="text-right">
                            {card?.funding
                                ? `${
                                      card.funding.charAt(0).toUpperCase() +
                                      card.funding.slice(1)
                                  } card`
                                : "Credit card"}
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className="flex items-center justify-between py-3 px-4 bg-gray-50 border-b border-gray-200 text-sm font-medium text-gray-600">
                        <div className="flex-1 basis-1/2">Payment method</div>
                        <div className="flex-1 basis-1/6 text-center">
                            Expiration Date
                        </div>
                        <div className="flex-1 basis-1/6 text-center">
                            Status
                        </div>
                        <div className="flex-1 basis-1/6 text-right">Type</div>
                    </div>
                    <div className="flex items-center justify-between py-3 px-4 border-b border-gray-100 hover:bg-gray-50">
                        {/* Payment Method */}
                        <div className="flex items-center space-x-3 basis-1/2">
                            {getCardIcon(card?.brand || "")}
                            <span className="font-medium capitalize">
                                {card?.brand === "jcb" ? "JCB" : card?.brand}{" "}
                                ****{card?.last4}
                            </span>
                        </div>

                        {/* Expiration Date */}
                        <div className="basis-1/6 text-center">
                            <span>
                                {formatExpiry(
                                    card?.expMonth || 0,
                                    card?.expYear || 0
                                )}
                            </span>
                        </div>

                        {/* Status */}
                        <div className="basis-1/6 text-center">
                            <span
                                className={`inline-flex items-center ${getStatusColor(
                                    status
                                )}`}
                            >
                                <div
                                    className={`w-2 h-2 rounded-full mr-2 ${
                                        status.toLowerCase() === "active"
                                            ? "bg-green-500"
                                            : status.toLowerCase() === "expired"
                                            ? "bg-red-500"
                                            : "bg-gray-500"
                                    }`}
                                ></div>
                                {status}
                            </span>
                        </div>

                        {/* Type */}
                        <div className="basis-1/6 text-right">
                            <span>
                                {card?.funding
                                    ? `${
                                          card.funding.charAt(0).toUpperCase() +
                                          card.funding.slice(1)
                                      } card`
                                    : "Credit card"}
                            </span>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
